import { ReactNode } from 'react';
import { TSessionDetails } from '@api/io/session.io';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useSessionThumbnail } from '@hooks/useImageUrl';
import { HeroContainer } from '@fe-learner-common/components/HeroContainer';
import { Breadcrumb } from '@fe-learner-common/components/Breadcrumb';
import { FaIcon, Tag } from '@fe-learner-common';
import { InstructorsPromo } from '@fe-learner-common/components/InstructorsPromo';
import { RichText } from '@fe-core-ui/src/components/RichText';
import { isProjectSelection } from '@fe-learner-common/api/schemas/sessionsSchema';
import { useApplyPageConfig } from '@routes/useApplyPageConfig';
import { useEnrollmentActions } from '@routes/session-details/hooks/useEnrollmentActions';
import { useSessionDetailsTags } from '@routes/session-details/hooks/useSessionDetailsTags';

export const SessionDetailsPage = ({
  sessionDetails,
  children,
}: {
  sessionDetails: TSessionDetails;
  children: ReactNode;
}) => {
  useApplyPageConfig();
  const intl = useIntl();

  const sessionImage = useSessionThumbnail({
    session: sessionDetails,
    size: 640,
  });

  const enrollmentActions = useEnrollmentActions({ sessionDetails });
  const tags = useSessionDetailsTags(sessionDetails);
  const isSelection = isProjectSelection(sessionDetails);

  return (
    <div>
      <HeroContainer
        courseImage={sessionImage}
        courseImageAlt={intl.formatMessage({
          id: 'learner.common.course.image',
        })}
        topSlot={
          <Breadcrumb
            rootTitle={intl.formatMessage({ id: 'catalog.directory.title' })}
            items={[
              {
                label: sessionDetails.name || '',
                href: `/session/${sessionDetails.id}`,
              },
            ]}
          />
        }
        background={{
          backdrop: sessionImage,
          image: sessionDetails?.bgImage,
          color: sessionDetails?.bgColor,
        }}
      >
        <div>
          <h1 className="lf:text-headline-sm">{sessionDetails?.name}</h1>
          <div className="lf:flex lf:flex-wrap lf:gap-3 lf:pt-2">
            {tags
              ? Object.entries(tags).map(([key, tag]) =>
                  tag ? (
                    <Tag
                      key={key}
                      type={tag.type}
                      className="lf:whitespace-nowrap"
                    >
                      <FaIcon width={12} icon={tag.icon} />
                      {tag.text}
                    </Tag>
                  ) : null,
                )
              : null}
          </div>
        </div>
        {isSelection && sessionDetails.description ? (
          <RichText className="lf:text-body-md lf:text-neutral-500">
            {sessionDetails.description}
          </RichText>
        ) : null}
        <div className="lf:flex lf:flex-col lf:gap-5 lf:lg:flex-row lf:lg:flex-nowrap">
          <div className="lf:flex lf:shrink-0 lf:flex-wrap lf:gap-5 [&>*]:flex-1">
            {enrollmentActions?.enrollmentAction}
            {enrollmentActions?.disenrollmentAction}
          </div>

          {!!enrollmentActions?.enrollmentAction &&
          !!enrollmentActions?.disenrollmentAction &&
          sessionDetails.trainers?.length ? (
            <div className="lf:hidden lf:border-r-2 lf:border-neutral-300 lf:lg:block" />
          ) : null}

          {!isSelection && sessionDetails.trainers?.length ? (
            <Link to="./instructors">
              <InstructorsPromo
                visiblePersons={sessionDetails.trainers
                  .slice(0, 3)
                  .map((trainer) => ({
                    name: trainer.name || '',
                    thumbnail: trainer.thumbnail || '',
                  }))}
                totalPersons={sessionDetails.trainers?.length}
              />
            </Link>
          ) : null}
        </div>
      </HeroContainer>
      {children}
    </div>
  );
};
